import type { ContestManageStore } from '../types/store';

type SectionKeyItemError = 'INVALID_SECTION_KEY' | 'SECTION_NOT_LOADED';

export type SectionKeyItem = {
  path: string[];
  key: string;
  type: string;
  error?: SectionKeyItemError;
  skipped?: boolean;
};

const getAllSectionKeysForDynamicSections = (
  store: ContestManageStore,
  sectionKey: string,
  path: string[] = []
): SectionKeyItem[] => {
  const section = store.sections[sectionKey];
  if (!section || section.type !== 'DynamicSections') {
    return [
      {
        path: [...path, sectionKey],
        key: sectionKey,
        type: 'DynamicSections',
        error: 'INVALID_SECTION_KEY',
      },
    ];
  }

  const sectionQuery = store.sectionsCache[section.latestQueryKey];
  if (!sectionQuery || sectionQuery.type !== 'DynamicSections' || !sectionQuery.data) {
    return [
      {
        path: [...path, sectionKey],
        key: sectionKey,
        type: 'DynamicSections',
        error: 'SECTION_NOT_LOADED',
      },
    ];
  }

  return sectionQuery.data.reduce((acc, sectionKey2) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const keys = getSectionKey(store, sectionKey2, [...path, sectionKey]);

    if (typeof keys === 'string') {
      acc.push(keys);
    } else if (Array.isArray(keys)) {
      acc.push(...keys);
    }

    return acc;
  }, []);
};

const getSectionKey = (
  store: ContestManageStore,
  sectionKey: string,
  path: string[] = []
): SectionKeyItem[] | null => {
  const section = store.sections[sectionKey];

  if (!section) {
    return null;
  }

  switch (section.type) {
    case 'SkippedSection': {
      return [
        {
          path: [...path, sectionKey],
          key: section.key,
          type: 'SkippedSection',
        },
      ];
    }
    case 'BaseSection': {
      return [
        {
          path: [...path, sectionKey],
          key: section.data.key,
          type: 'BaseSection',
        },
      ];
    }
    case 'DynamicSection': {
      const sectionQuery = store.sectionsCache[section.latestQueryKey];

      if (!sectionQuery || sectionQuery.type !== 'DynamicSection' || !sectionQuery.data) {
        return [
          {
            path: [...path, sectionKey],
            key: section.key,
            type: 'DynamicSection',
            error: 'SECTION_NOT_LOADED',
          },
        ];
      }

      return [
        {
          path: [...path, sectionKey],
          key: sectionQuery.data.key,
          type: 'DynamicSection',
        },
      ];
    }
    case 'DynamicSections': {
      return getAllSectionKeysForDynamicSections(store, sectionKey, path);
    }
    default: {
      return null;
    }
  }
};

export const getAllSectionKeys = (store: ContestManageStore): SectionKeyItem[] =>
  store.sectionsKeys.reduce((acc, sectionKey) => {
    const keys = getSectionKey(store, sectionKey);

    if (keys) {
      acc.push(...keys);
    }

    return acc;
  }, []);
